// src/components/Layout.js
import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from './Sidebar';

export default function Layout({ children, isLoggedIn, currentStaff, onLogout, onNavigate }) {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    // Load collapsed state from localStorage
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  
  // Sample rooms data
  const [rooms] = useState([
    { 
      id: 101, 
      floor: 1, 
      roomType: 'Deluxe', 
      isProcessed: false, 
      lastCleaned: null,
      assignedStaff: 'John Doe',
      checklist: [
        { item: 'Bedline', completed: false },
        { item: 'TV unit', completed: false },
        { item: 'Bathroom', completed: false }
      ]
    },
    { 
      id: 102, 
      floor: 1, 
      roomType: 'Standard', 
      isProcessed: true, 
      lastCleaned: '2024-01-15T10:30:00',
      assignedStaff: 'Sarah Smith',
      checklist: [
        { item: 'Bedline', completed: true },
        { item: 'TV unit', completed: true },
        { item: 'Bathroom', completed: true }
      ]
    },
    { 
      id: 201, 
      floor: 2, 
      roomType: 'Suite', 
      isProcessed: false, 
      lastCleaned: null,
      assignedStaff: 'Mike Johnson',
      checklist: [
        { item: 'Bedline', completed: false },
        { item: 'TV unit', completed: false },
        { item: 'Bathroom', completed: true }
      ]
    },
    { 
      id: 202, 
      floor: 2, 
      roomType: 'Standard', 
      isProcessed: false, 
      lastCleaned: null,
      assignedStaff: null,
      checklist: [
        { item: 'Bedline', completed: false },
        { item: 'TV unit', completed: false },
        { item: 'Bathroom', completed: false }
      ]
    },
  ]);

  // Save collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  // Handle responsive behavior
  useEffect(() => {
    const checkScreenSize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      
      if (!mobile) {
        setIsSidebarOpen(false);
      }
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Handle escape key to close sidebar
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isSidebarOpen) {
        setIsSidebarOpen(false);
      }
    };
    
    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [isSidebarOpen]);

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen(prev => !prev);
  }, []);

  const closeSidebar = useCallback(() => {
    setIsSidebarOpen(false);
  }, []);

  const handleNavigate = useCallback((pageId) => {
    onNavigate(pageId);
    if (isMobile) {
      closeSidebar();
    }
  }, [onNavigate, isMobile, closeSidebar]);

  const toggleSidebarCollapse = useCallback(() => {
    setIsSidebarCollapsed(prev => !prev);
  }, []);

  if (!isLoggedIn) {
    return <div className="min-h-screen bg-gray-50">{children}</div>;
  }

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Mobile Menu Button */}
      <button
        onClick={toggleSidebar}
        className={`fixed z-50 p-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 lg:hidden ${
          isSidebarOpen ? 'left-64' : 'left-4'
        } top-4`}
        aria-label="Toggle Menu"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          {isSidebarOpen ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          )}
        </svg>
      </button>

      {/* Sidebar */}
      <Sidebar
        rooms={rooms}
        selectedRoomId={selectedRoomId}
        onSelectRoom={setSelectedRoomId}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={toggleSidebarCollapse}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onNavigate={handleNavigate}
        isOpen={isSidebarOpen}
        onClose={closeSidebar}
        isMobile={isMobile}
      />
      
      {/* Main Content */}
      <div className={`flex-1 flex flex-col overflow-hidden w-full transition-all duration-300 ${
        !isMobile && !isSidebarCollapsed ? 'lg:ml-0' : ''
      }`}>
        <header className="bg-white shadow-sm border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            <div className={`transition-all duration-300 ${isMobile ? 'ml-12' : ''}`}>
              <h1 className="text-lg sm:text-xl font-semibold text-gray-800">
                Housekeeping Management System
              </h1>
              <p className="text-xs sm:text-sm text-gray-500">
                Welcome back, {currentStaff?.name || 'Staff'}
              </p>
            </div>
            <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4">
              <div className="flex items-center gap-2 sm:hidden">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-md">
                  <span className="text-sm text-white">👤</span>
                </div>
                <div className="text-left">
                  <p className="text-xs font-medium text-gray-700">{currentStaff?.name?.split(' ')[0] || 'User'}</p>
                  <p className="text-xs text-gray-500">Staff</p>
                </div>
              </div>
              
              <div className="hidden sm:flex sm:items-center sm:gap-4">
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-700">{currentStaff?.name || 'User'}</p>
                  <p className="text-xs text-gray-500">ID: {currentStaff?.id || 'N/A'}</p>
                </div>
              </div>
              
              <button
                onClick={onLogout}
                className="px-3 sm:px-4 py-1.5 sm:py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition font-medium"
              >
                Logout
              </button>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-auto p-4 sm:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}